/**
 * MePage — `/app/me`. Profile + account.
 *
 * Shows the account name, the personal access token ("this is your key" +
 * copy), the Free tier with an upgrade option, an Install button (PWA), and
 * sign-out (clears the token and returns to the front door).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Download, LogOut, KeyRound, Check } from 'lucide-react';

import {
  getMonitorToken,
  getWorkspaceName,
  MONITOR_TOKEN_KEY,
  MONITOR_INGEST_URL_KEY,
  MONITOR_WORKSPACE_KEY,
} from '@/pages/monitor/monitorClient';
import { c } from './theme';
import { PageHeader } from './ui';
import { useInstallPrompt } from './useInstallPrompt';

export function MePage() {
  const navigate = useNavigate();
  const { canInstall, installed, promptInstall } = useInstallPrompt();
  const token = getMonitorToken() ?? '';
  const name = getWorkspaceName() ?? 'My Monitor';
  const [copied, setCopied] = useState(false);

  async function copyToken() {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked; token is visible to copy by hand */
    }
  }

  function signOut() {
    localStorage.removeItem(MONITOR_TOKEN_KEY);
    localStorage.removeItem(MONITOR_INGEST_URL_KEY);
    localStorage.removeItem(MONITOR_WORKSPACE_KEY);
    navigate('/app/start', { replace: true });
  }

  const masked = token ? `${token.slice(0, 6)}••••••••${token.slice(-4)}` : '';

  return (
    <div>
      <PageHeader title={name} sub="Your account, your key, your control." />

      <div style={{ padding: '14px 18px 8px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Tier */}
        <div
          style={{
            background: c.surface,
            border: `1px solid ${c.rim}`,
            borderRadius: 16,
            padding: '16px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: c.inkSubtle, fontWeight: 600 }}>Your plan</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: c.ink }}>Free</div>
          </div>
          <button
            type="button"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              minHeight: 44,
              padding: '0 16px',
              fontSize: 14,
              fontWeight: 800,
              color: '#ffffff',
              background: c.accent,
              border: 'none',
              borderRadius: 12,
              cursor: 'pointer',
            }}
          >
            <Crown size={16} /> Go unlimited — $9.90/mo
          </button>
        </div>

        {/* Access key */}
        <div
          style={{
            background: c.surface,
            border: `1px solid ${c.rim}`,
            borderRadius: 16,
            padding: '16px 18px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              fontWeight: 700,
              color: c.ink,
              marginBottom: 6,
            }}
          >
            <KeyRound size={16} style={{ color: c.accent }} /> This is your key
          </div>
          <p style={{ fontSize: 13, color: c.inkMuted, margin: '0 0 12px', lineHeight: 1.5 }}>
            Keep it safe. Anyone with this key can reach your account. We'll never ask you for it.
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: c.canvas,
              border: `1px solid ${c.rim}`,
              borderRadius: 12,
              padding: '10px 12px',
            }}
          >
            <code
              style={{
                flex: 1,
                fontSize: 13,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: c.ink,
              }}
            >
              {masked}
            </code>
            <button
              type="button"
              onClick={copyToken}
              style={{
                flexShrink: 0,
                minHeight: 40,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                fontWeight: 700,
                color: c.accent,
                background: c.surface,
                border: `1px solid ${c.rim}`,
                borderRadius: 10,
                padding: '0 14px',
                cursor: 'pointer',
              }}
            >
              {copied ? <Check size={15} /> : null}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Install */}
        <button
          type="button"
          onClick={() => promptInstall()}
          disabled={!canInstall}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            minHeight: 56,
            padding: '0 18px',
            fontSize: 15,
            fontWeight: 700,
            color: installed ? c.inkSubtle : c.ink,
            background: c.surface,
            border: `1px solid ${c.rim}`,
            borderRadius: 16,
            cursor: canInstall ? 'pointer' : 'default',
            textAlign: 'left',
          }}
        >
          <Download size={20} style={{ color: c.accent }} />
          <span style={{ flex: 1 }}>
            {installed
              ? 'Installed on this device ✓'
              : canInstall
                ? 'Install Monitor on your phone'
                : 'Add to Home Screen from your browser menu'}
          </span>
        </button>

        {/* Sign out */}
        <button
          type="button"
          onClick={signOut}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            minHeight: 52,
            padding: '0 18px',
            fontSize: 15,
            fontWeight: 700,
            color: c.danger,
            background: c.surface,
            border: `1px solid ${c.rim}`,
            borderRadius: 16,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <LogOut size={20} />
          <span style={{ flex: 1 }}>Sign out</span>
        </button>

        <p style={{ fontSize: 12, color: c.inkSubtle, textAlign: 'center', margin: '8px 0 0' }}>
          Made by Growing Intelligence. Your data stays yours.
        </p>
      </div>
    </div>
  );
}

export default MePage;
